<template>
  <Pane
    :value="checklistVersioningPane.isOpen"
    class="ChecklistVersioningPane"
    data-locator="checklist-versioning-pane"
    temporary
    :tabs="tabs"
    v-model:current-tab="tab"
    @update:modelValue="onClosePane"
  >
    <template #content-manage>
      <!-- Custom overlay to manage overlay with pane and other dialogs -->
      <v-overlay v-if="isDocumentOnGoingEditionDialogOpen"></v-overlay>

      <DocumentVersioning :loading="attachedFileVersionsPending || postAttachedFileVersionsPending"
                          :selected-documents="checklistVersioningPane.documents"
                          @setIsDocumentOnGoingEditionDialogOpen="isDocumentOnGoingEditionDialogOpen = $event"
                          @add-version="onAddVersion"
                          @add-version-from-imanage="onAddVersionFromImanage"
                          @close-pane="onClosePane"
      />
    </template>
    <template #content-history>
      <DocumentVersioningHistory :selected-documents="checklistVersioningPane.documents"
                                  @share-version="onShareVersion"
      />
    </template>
  </Pane>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import DocumentVersioning from '@/common/document-versioning/DocumentVersioning.vue'
import DocumentVersioningHistory from '@/common/document-versioning/DocumentVersioningHistory.vue'
import Pane from '@/common/Pane.vue'
import { toast } from '@/common/utils/toast'
import {
  CHANGE_CHECKLIST_VERSIONING_PANE_TAB,
  CLOSE_CHECKLIST_VERSIONING_PANE,
  GET_GLOBAL_TASK_FOLDER,
  GET_TASK_FOLDER,
  POST_ATTACHED_FILE_VERSION,
  SET_CHECKLIST_VERSIONING_PANE,
} from '@/store/modules/checklist/action_types'
import { POST_VERSION_FROM_IMANAGE, SET_TABLE_DOCUMENTS_PANE } from '@/store/modules/documents/action_types'
const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistVersioningPane'

export default {
  name: 'ChecklistVersioningPane',
  components: {
    DocumentVersioning,
    DocumentVersioningHistory,
    Pane,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      isDocumentOnGoingEditionDialogOpen: false,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm', 'currentUserRights']),
    ...mapState('documents', ['numberingEnabled']),
    ...mapState('checklist', ['attachedFileVersionsPending', 'checklistVersioningPane', 'globalAttachedFilesDialogIsOpen', 'postAttachedFileVersionsPending']),
    canManageVersions () {
      return this.isCurrentUserPm || this.currentUserRights.canUploadVersions
    },
    tab: {
      get () {
        return this.checklistVersioningPane.tab
      },
      set (val) {
        this.CHANGE_CHECKLIST_VERSIONING_PANE_TAB(val)
      },
    },
    tabs () {
      const tabs = []
      if (this.checklistVersioningPane.documents.length === 1 && this.checklistVersioningPane.documents[0].type === 'file') {
        if (this.canManageVersions) {
          tabs.push({
            title: this.$t(`${TRANSLATION_KEY_PREFIX}.tabManage`),
            id: 'manage',
          })
        }

        if (this.canManageVersions || this.checklistVersioningPane.documents[0].nbVersions > 1) {
          tabs.push({
            title: this.canManageVersions ? this.$t(`${TRANSLATION_KEY_PREFIX}.tabHistory`) : this.$t(`${TRANSLATION_KEY_PREFIX}.tabHistoryGuests`),
            id: 'history',
          })
        }
      }
      return tabs
    },
  },
  methods: {
    ...mapActions('checklist', [
      CHANGE_CHECKLIST_VERSIONING_PANE_TAB,
      CLOSE_CHECKLIST_VERSIONING_PANE,
      GET_GLOBAL_TASK_FOLDER,
      GET_TASK_FOLDER,
      POST_ATTACHED_FILE_VERSION,
      SET_CHECKLIST_VERSIONING_PANE,
    ]),
    ...mapActions('documents', [POST_VERSION_FROM_IMANAGE, SET_TABLE_DOCUMENTS_PANE]),
    async onAddVersion (data) {
      try {
        await this.POST_ATTACHED_FILE_VERSION({
          mnemo: this.mnemo,
          documentId: this.checklistVersioningPane.documents[0].id,
          data: data,
        })

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.postVersionSuccess`))

        if (this.globalAttachedFilesDialogIsOpen) {
          this.GET_GLOBAL_TASK_FOLDER(this.mnemo)
        } else {
          this.GET_TASK_FOLDER({
            mnemo: this.mnemo,
            folderId: this.checklistVersioningPane.documents[0].parent.id,
          })
        }

        this.onClosePane()
      } catch (e) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.postVersionError`))
      }
    },
    async onAddVersionFromImanage (data) {
      try {
        await this.POST_VERSION_FROM_IMANAGE({
          mnemo: this.mnemo,
          data: data,
        })

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.postVersionFromImanageSuccess`))

        if (this.globalAttachedFilesDialogIsOpen) {
          this.GET_GLOBAL_TASK_FOLDER(this.mnemo)
        } else {
          this.GET_TASK_FOLDER({
            mnemo: this.mnemo,
            folderId: this.checklistVersioningPane.documents[0].parent.id,
          })
        }

        this.onClosePane()
      } catch (error) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.postVersionFromImanageError`))
      }
    },
    onClosePane (value) {
      if (!value) {
        this.CLOSE_CHECKLIST_VERSIONING_PANE()
      }
    },
    onShareVersion (data) {
      const currentDocument = { ...this.checklistVersioningPane.documents[0] }

      this.CLOSE_CHECKLIST_VERSIONING_PANE()
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        tab: 'share',
        documents: [data],
        checklistFilesParent: currentDocument?.parent,
        forcePmSelection: true,
        previousDocument: this.checklistVersioningPane.documents[0],
        onCloseCallback: () => {
          this.SET_CHECKLIST_VERSIONING_PANE({
            isOpen: true,
            documents: [currentDocument],
            tab: 'history',
          })
        },
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";
@media #{map-get($display-breakpoints, 'sm-and-up')} {
  .v-navigation-drawer {
    min-width: 480px !important;
  }
}
</style>
